import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocProgressSpinner } from 'commons/components/components-wrapper/poc-progress-spinner.component';
import type { PocInputValidity } from 'commons/types';
import { Input, ModalDialog } from 'commons/components';
import { PopupExtendedContext } from 'core/contexts/popup-extended.context';
import { useTranslation } from 'core/i18n/i18n';
import { useContext, useMemo, useState } from 'react';
import { PopupProps } from 'react-popup-manager';
import { getSaveFilterValidationMessages } from './save-filter-modal.validation';

type FilterData = {
    name: string | undefined;
};

type FilterInputValidity = {
    name: PocInputValidity;
};

interface SaveFilterModalComponentProps extends PopupProps {
    initialFilterName?: string;
    onValidity: (field: string, validity: PocInputValidity) => void;
    onCancel: VoidFunction;
    onSave: (filterName: string) => Promise<boolean>;
}

export const SaveFilterModalComponent = (props: SaveFilterModalComponentProps) => {
    const { initialFilterName, onSave, onCancel, onValidity, isOpen = true, onClose } = props;

    const [filterData, setFilterData] = useState<FilterData>({ name: initialFilterName });
    const [savingFilter, setSavingFilter] = useState<boolean>(false);
    const context = useContext(PopupExtendedContext);
    const { t } = useTranslation();

    const initialFilterTranslation = useMemo(
        () =>
            initialFilterName
                ? t('commons.components.rename-filter-modal.button')
                : t(`commons.components.save-edit-filter-modal.save-button`),
        [initialFilterName, t]
    );

    const handleOnClick = () => {
        setSavingFilter(true);
        onSave(filterData.name!)
            .then((result) => {
                if (result) {
                    onClose?.();
                }
            })
            .finally(() => setSavingFilter(false));
    };

    const handleChangeData = (field: string, value: string | null) => {
        setFilterData({ ...filterData, [field]: value });
    };

    const handleCancel = () => {
        onCancel();
        onClose?.();
    };

    return (
        <ModalDialog
            isForm
            isOpen={isOpen}
            title={
                initialFilterName
                    ? t(`commons.components.rename-filter-modal.title`)
                    : t(`commons.components.save-filter-modal.title`)
            }
            footerOptions={
                <PocButton
                    type="submit"
                    variant="primary"
                    size="medium"
                    onClick={savingFilter ? undefined : handleOnClick}
                    disabled={!filterData.name || filterData.name?.trim().length === 0}
                    data-testid="save-filter-submit"
                >
                    {savingFilter ? (
                        <PocProgressSpinner size="xSmall" style={{ display: 'block' }} />
                    ) : (
                        initialFilterTranslation
                    )}
                </PocButton>
            }
            cancelButtonDisabled={savingFilter}
            onClose={handleCancel}
        >
            <Input
                name={'name'}
                label={
                    initialFilterName
                        ? t(`commons.components.rename-filter-modal.input-label`)
                        : t(`commons.components.save-edit-filter-modal.input-label`)
                }
                placeholder={t(`commons.components.save-edit-filter-modal.input-placeholder`)}
                variant="body1"
                maxlength={32}
                spaceForError
                validity={(context.data.filterSaveValidity as FilterInputValidity)?.name}
                customValidationCode={
                    Array.isArray(context.data.filterSaveErrors) && context.data.filterSaveErrors[0]?.extensions?.type
                        ? context.data.filterSaveErrors[0].extensions.type
                        : ''
                }
                value={filterData.name}
                required
                validationMessages={getSaveFilterValidationMessages(t).saveFilter}
                onValidity={onValidity}
                onChange={(field, value) => handleChangeData(field, value)}
            />
        </ModalDialog>
    );
};
